<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { copyTextToClipboard } from 'shared/helpers/clipboard';
import VisitSchedulingLinksAPI from 'custom/dashboard/api/visitSchedulingLinks';

import NextButton from 'dashboard/components-next/button/Button.vue';
import DropdownMenu from 'dashboard/components-next/dropdown-menu/DropdownMenu.vue';

// Copies the conversation's public visit scheduling link without sending anything, for the
// agent who wants to paste it into a message of their own. The links come from the backend
// (see VisitScheduling::LinkBuilder) so they follow exactly the rules the public page
// enforces — which is also why a conversation the page would reject answers 404 here and
// the agent is told so instead of being handed a dead link.
//
// When the property or the lead already tells sale from rental, the link is copied straight
// away. Only when nothing does — and both deals take visits — does a menu ask the agent which
// one, instead of silently handing over the rental link the page would fall back to.
const props = defineProps({
  conversationId: {
    type: [Number, String],
    required: true,
  },
});

const { t } = useI18n();

const isLoading = ref(false);
const menuItems = ref([]);
const isOpen = computed(() => menuItems.value.length > 0);

const kindLabel = kind => t(`MACROS.VISIT_SCHEDULING_LINK.KINDS.${kind}`);

const closeMenu = () => {
  menuItems.value = [];
};

const menuSections = computed(() => [
  {
    title: t('MACROS.VISIT_SCHEDULING_LINK.MENU_TITLE'),
    items: menuItems.value,
  },
]);

const copyLink = async ({ url, kind }) => {
  closeMenu();
  await copyTextToClipboard(url);
  useAlert(t('MACROS.VISIT_SCHEDULING_LINK.COPIED', { kind: kindLabel(kind) }));
};

const onClick = async () => {
  if (isOpen.value) {
    closeMenu();
    return;
  }

  isLoading.value = true;
  try {
    const { data } = await VisitSchedulingLinksAPI.show(props.conversationId);
    const choices = Object.entries(data.links);

    if (data.source !== 'default' || choices.length < 2) {
      await copyLink({ url: data.url, kind: data.kind });
      return;
    }

    menuItems.value = choices.map(([kind, url]) => ({
      label: kindLabel(kind),
      value: kind,
      action: 'copy',
      icon: kind === 'sell' ? 'i-lucide-key-round' : 'i-lucide-house',
      url,
      kind,
    }));
  } catch (error) {
    useAlert(t('MACROS.VISIT_SCHEDULING_LINK.ERROR'));
  } finally {
    isLoading.value = false;
  }
};
</script>

<template>
  <!-- Not `relative` on purpose: the menu takes the width of the macro row (MacroItem's root is
       `relative`), since the contact panel clips anything wider than itself. -->
  <div v-on-clickaway="closeMenu">
    <NextButton
      v-tooltip.left-start="$t('MACROS.VISIT_SCHEDULING_LINK.TOOLTIP')"
      data-testid="copy-visit-scheduling-link"
      icon="i-lucide-link"
      slate
      faded
      xs
      :is-loading="isLoading"
      @click="onClick"
    />
    <DropdownMenu
      v-if="isOpen"
      data-testid="visit-scheduling-link-menu"
      :menu-sections="menuSections"
      class="inset-x-0 mb-1 bottom-full"
      @action="copyLink"
    />
  </div>
</template>
